import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Alert, Col, Input, Modal, Row } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from 'src/store'
import { AssignTree, Id } from 'src/types'
import { getGroupTrees } from 'src/core/store/ducks/groupTrees/actions'
import { getTree, getTreeSuccess } from 'src/core/store/ducks/tree/actions'
import { NodeColor } from 'src/components/Tree/Nodes'
import { PageHeader } from 'src/components/Ant'
import { Tree } from 'src/components/Tree'
import * as UsersService from 'src/services/api/users'
import useDocumentTitle from 'src/hooks/use-document-title'

import * as CSS from './styles'
import ModalTreeNode from './modal-tree-node'
import TableList from './components/table-list'
import TreeSideOptions from './components/tree-side-options'

export type PropsTreeSelected = {
  chained: boolean
  id: Id
  name: string
  p_assign_tree_type: string
}

function updateChildrens(node: AssignTree) {
  return function update(acc: AssignTree[], cur: AssignTree): AssignTree[] {
    const { childrens = [] } = cur
    if (cur.assign_tree_id === node.parent_id) {
      return [...acc, { ...cur, childrens: [...childrens, node] }]
    }

    return [
      ...acc,
      { ...cur, childrens: childrens.reduce(updateChildrens(node), []) }
    ]
  }
}

export default function TreeManagement() {
  const { groupTrees, tree } = useSelector((state: ApplicationState) => ({
    groupTrees: state.groupTrees,
    tree: state.tree
  }))

  useDocumentTitle('web.tree.title')
  const dispatch = useDispatch()

  const isSubscribed = useRef(true)
  const refTimeout = useRef<NodeJS.Timeout>(null)
  const refFormikNode = useRef(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [listUsers, setListUsers] = useState([])
  const [hasError, setHasError] = useState<string>('')
  const [filter, setFilter] = useState<string>('')
  const [filterRole, setFilterRole] = useState([])

  const [usersFiltered, setUsersFiltered] = useState([])
  const [usersFilteredModal, setUsersFilteredModal] = useState([])

  const [visibleModal, setVisibleModal] = useState<boolean>(false)
  const [groupTreesList, setGroupTreesList] = useState(null)
  const [treesSelected, setTreeSelected] = useState<PropsTreeSelected>({
    id: null,
    name: '',
    chained: false,
    p_assign_tree_type: ''
  })
  const { t } = useTranslation()

  useEffect(() => {
    if (treesSelected.id) {
      dispatch(getGroupTrees({ assign_tree_id: treesSelected.id }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treesSelected, groupTrees.sendGroupTreesLoading])

  useEffect(() => {
    dispatch(getTree())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setGroupTreesList(null)
    if (groupTrees.list) {
      setGroupTreesList(groupTrees.list)
    }
  }, [groupTrees.list, treesSelected])

  const getUsers = useCallback(
    async (_page = 1, _filter = '') => {
      setIsLoading(true)
      setHasError('')

      const response = await UsersService.getAll({
        filter: _filter,
        page: _page,
        pageSize: 20,
        withInactive: true
      })

      if (!isSubscribed.current) {
        return
      }
      setIsLoading(false)
      if (response.status === 200) {
        setListUsers(response.data.users)
        setHasError('')
        return
      }
      const genericError = t('web.user.errors.on-search')
      if (response.status === 500 || !response.error) {
        setHasError(genericError)
        return
      }

      setHasError(response?.error || genericError)
    },
    [t]
  )

  const usersRefactor = useMemo(() => {
    if (groupTreesList) {
      return [...new Set(groupTreesList?.map((item: any) => item.user_id))].map(
        (idUser: any) => ({
          assign_tree_id: groupTreesList.filter(
            (d: any) => d.user_id === idUser
          )[0].assign_tree_id,
          role_type: groupTreesList
            .filter((d: any) => d.user_id === idUser)
            .map((u: any) => u.role_type),
          user_id: idUser,
          user: groupTreesList.filter((d: any) => d.user_id === idUser)[0].user
        })
      )
    }
  }, [groupTreesList])

  useEffect(() => {
    if (filterRole.length > 0) {
      const filter = usersFiltered.filter(user => {
        let on = filterRole.map(filter =>
          user.role_type.includes(filter.toUpperCase())
        )
        return !on.includes(false)
      })

      setUsersFiltered(filter)
    }

    if (usersRefactor && filterRole.length === 0) {
      setUsersFiltered(usersRefactor)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole, usersRefactor])

  useEffect(() => {
    const filter = listUsers.filter(
      user => !usersRefactor.find(filter => user.user_id === filter.user_id)
    )
    setUsersFilteredModal(filter)
  }, [listUsers, usersRefactor])

  useEffect(() => {
    return () => {
      isSubscribed.current = false
    }
  }, [])

  useEffect(() => {
    clearTimeout(refTimeout.current)
    refTimeout.current = setTimeout(() => {
      getUsers(1, filter)
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  if (tree.error) {
    return (
      <>
        <PageHeader title="web.tree.title" />
        <CSS.Alert message="web.tree.load-error" />
      </>
    )
  }

  function addNode(data: AssignTree = null): void {
    if (!data) {
      refFormikNode.current?.resetForm({})
      return
    }

    let form: AssignTree & { parent_name: string } = {
      ...data,
      assign_tree_id: null,
      assign_tree_name: '',
      childrens: [],
      code: '',
      parent_id: data.assign_tree_id,
      parent_name: data.assign_tree_name
    }

    refFormikNode.current?.resetForm(form)
  }

  function updateNode(node: AssignTree): void {
    if (isSubscribed.current) {
      const updatedTree = tree.list.reduce(updateChildrens(node), [])
      dispatch(getTreeSuccess(updatedTree))
      setVisibleModal(false)
    }
  }

  const valuedNodes = []
  if (treesSelected?.id) {
    valuedNodes.push({
      assign_tree_id: treesSelected.id,
      color: NodeColor.DISABLED
    })
  }
  return (
    <>
      <PageHeader title="web.tree.title" />
      <Row gutter={12}>
        <Col span={12}>
          <CSS.Div>
            <Tree
              addNode={addNode}
              setTreeSelected={setTreeSelected}
              valuedNodes={valuedNodes}
            />
          </CSS.Div>
        </Col>
        <Col span={12}>
          <TreeSideOptions
            dataSource={usersFiltered}
            filterRole={filterRole}
            loading={groupTrees.loading || false}
            pagination={false}
            setFilterRole={setFilterRole}
            setVisibleModal={setVisibleModal}
            treesSelected={treesSelected}
            setTreeSelected={setTreeSelected}
          />
        </Col>
      </Row>
      <Modal
        bodyStyle={{ padding: '0px' }}
        okButtonProps={{ hidden: true }}
        onCancel={() => setVisibleModal(false)}
        style={{ top: '40px' }}
        title={t('common.actions.add') + ` / ${treesSelected.name}`}
        visible={visibleModal}
        width={1000}
      >
        <Row style={{ margin: '15px 12px' }}>
          <Col span={16}>
            {hasError && <Alert message={hasError} showIcon type="error" />}
          </Col>
          <Col span={8}>
            <Input
              onChange={e => setFilter(e.target.value)}
              placeholder={t('common.filters')}
              value={filter}
            />
          </Col>
        </Row>
        <TableList
          dataSource={usersFilteredModal}
          isModal={true}
          loading={isLoading}
          onChange={page => {
            getUsers(page.current, filter)
          }}
          pagination={false}
          treesSelected={treesSelected}
          usersFiltered={usersFiltered}
        />
      </Modal>
      <ModalTreeNode refFormik={refFormikNode} updateNode={updateNode} />
    </>
  )
}
